import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { assignmentsAPI, incidentsAPI, patientsAPI, usersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import AccessibleModal from '../components/common/AccessibleModal';

const INCIDENT_LOCATION_OPTIONS = [
  'Salle d’activités',
  'Salle à manger',
  'Couloir',
  'Toilettes',
  'Entrée / réception',
  'Escalier / ascenseur',
  'Cour extérieure / jardin',
  'Véhicule de transport',
  'Autre (saisie manuelle)',
];

const MANUAL_LOCATION_VALUE = 'Autre (saisie manuelle)';
const incidentTypeLabel = (type, tr) => {
  const labels = {
    fall: tr('Chute', 'Fall'),
    injury: tr('Blessure', 'Injury'),
    medical: tr('Médical', 'Medical'),
    behavioral: tr('Comportement', 'Behavioral'),
    medication: tr('Médication', 'Medication'),
    other: tr('Autre', 'Other'),
  };
  return labels[type] || type;
};
const severityLabel = (severity, tr) => {
  const labels = {
    low: tr('Faible', 'Low'),
    medium: tr('Moyenne', 'Medium'),
    high: tr('Élevée', 'High'),
    critical: tr('Critique', 'Critical'),
  };
  return labels[severity] || severity;
};
const statusLabel = (status, tr) => {
  const labels = {
    open: tr('Ouvert', 'Open'),
    investigating: tr('En investigation', 'Investigating'),
    resolved: tr('Résolu', 'Resolved'),
    closed: tr('Fermé', 'Closed'),
  };
  return labels[status] || status;
};
const locationLabel = (value, tr) => {
  const labels = {
    'Salle d’activités': tr('Salle d’activités', 'Activity room'),
    'Salle à manger': tr('Salle à manger', 'Dining room'),
    Couloir: tr('Couloir', 'Hallway'),
    Toilettes: tr('Toilettes', 'Restroom'),
    'Entrée / réception': tr('Entrée / réception', 'Entrance / reception'),
    'Escalier / ascenseur': tr('Escalier / ascenseur', 'Stairs / elevator'),
    'Cour extérieure / jardin': tr('Cour extérieure / jardin', 'Outdoor yard / garden'),
    'Véhicule de transport': tr('Véhicule de transport', 'Transport vehicle'),
    'Autre (saisie manuelle)': tr('Autre (saisie manuelle)', 'Other (manual entry)'),
  };
  return labels[value] || value;
};

const getLocationPreset = (value) => {
  if (!value) return '';
  if (INCIDENT_LOCATION_OPTIONS.includes(value)) return value;
  return MANUAL_LOCATION_VALUE;
};

const badgeClassBySeverity = (severity) => {
  if (severity === 'critical') return 'badge badge-danger';
  if (severity === 'high') return 'badge badge-warning';
  if (severity === 'medium') return 'badge badge-info';
  return 'badge badge-gray';
};

const badgeClassByStatus = (status) => {
  if (status === 'resolved' || status === 'closed') return 'badge badge-success';
  if (status === 'investigating') return 'badge badge-warning';
  return 'badge badge-primary';
};

const toInputDatetime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
};

const IncidentModal = ({ incident, patients, staff, readOnly, canAssignStaff, onClose, onSave }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const tr = (fr, en) => (isFr ? fr : en);
  const [form, setForm] = useState({
    patientId: incident?.patient_id || '',
    incidentType: incident?.incident_type || 'fall',
    severity: incident?.severity || 'medium',
    occurredAt: toInputDatetime(incident?.occurred_at),
    location: incident?.location || '',
    description: incident?.description || '',
    immediateActions: incident?.immediate_actions || '',
    witnessNames: incident?.witness_names || '',
    followupRequired: !!incident?.followup_required,
    followupDate: incident?.followup_date ? String(incident.followup_date).split('T')[0] : '',
    assignedTo: incident?.assigned_to || '',
    status: incident?.status || 'open',
    resolutionNotes: incident?.resolution_notes || '',
    locationPreset: getLocationPreset(incident?.location || ''),
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setLoading(true);
    try {
      const payload = {
        patientId: form.patientId,
        incidentType: form.incidentType,
        severity: form.severity,
        occurredAt: form.occurredAt,
        location: form.location || null,
        description: form.description,
        immediateActions: form.immediateActions || null,
        witnessNames: form.witnessNames || null,
        followupRequired: form.followupRequired,
        followupDate: form.followupRequired && form.followupDate ? form.followupDate : null,
        assignedTo: form.assignedTo || null,
        status: form.status,
        resolutionNotes: form.resolutionNotes || null,
      };
      await incidentsAPI.update(incident.id, {
        ...payload,
      });
      onSave();
    } catch (err) {
      alert(err?.response?.data?.message || tr('Erreur lors de la mise à jour de l’incident', 'Error while updating incident'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccessibleModal
      title={readOnly ? tr('Consulter incident', 'View incident') : tr('Modifier incident', 'Edit incident')}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {readOnly ? tr('Fermer', 'Close') : tr('Annuler', 'Cancel')}
          </button>
          {!readOnly && (
            <button type="submit" form="incident-form" className="btn btn-primary" disabled={loading}>
              {loading ? tr('Enregistrement...', 'Saving...') : tr('Enregistrer', 'Save')}
            </button>
          )}
        </>
      )}
    >
        <form id="incident-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Usager', 'Patient')} *</label>
                <select
                  className="form-select"
                  value={form.patientId}
                  onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))}
                  required
                  disabled={readOnly}
                >
                  <option value="">{tr('Sélectionner', 'Select')}</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Type', 'Type')} *</label>
                <select
                  className="form-select"
                  value={form.incidentType}
                  onChange={e => setForm(f => ({ ...f, incidentType: e.target.value }))}
                  disabled={readOnly}
                >
                  <option value="fall">{tr('Chute', 'Fall')}</option>
                  <option value="injury">{tr('Blessure', 'Injury')}</option>
                  <option value="medical">{tr('Médical', 'Medical')}</option>
                  <option value="behavioral">{tr('Comportement', 'Behavioral')}</option>
                  <option value="medication">{tr('Médication', 'Medication')}</option>
                  <option value="other">{tr('Autre', 'Other')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Sévérité', 'Severity')} *</label>
                <select
                  className="form-select"
                  value={form.severity}
                  onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                  disabled={readOnly}
                >
                  <option value="low">{tr('Faible', 'Low')}</option>
                  <option value="medium">{tr('Moyenne', 'Medium')}</option>
                  <option value="high">{tr('Élevée', 'High')}</option>
                  <option value="critical">{tr('Critique', 'Critical')}</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Date/heure', 'Date/time')} *</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={form.occurredAt}
                  onChange={e => setForm(f => ({ ...f, occurredAt: e.target.value }))}
                  required
                  disabled={readOnly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Lieu', 'Location')}</label>
                <select
                  className="form-select"
                  value={form.locationPreset}
                  onChange={e => {
                    const preset = e.target.value;
                    setForm(f => ({
                      ...f,
                      locationPreset: preset,
                      location: preset && preset !== MANUAL_LOCATION_VALUE ? preset : '',
                    }));
                  }}
                  disabled={readOnly}
                >
                  <option value="">{tr('Non précisé', 'Not specified')}</option>
                  {INCIDENT_LOCATION_OPTIONS.map(loc => (
                    <option key={loc} value={loc}>{locationLabel(loc, tr)}</option>
                  ))}
                </select>
                {form.locationPreset === MANUAL_LOCATION_VALUE && (
                  <input
                    className="form-input"
                    style={{ marginTop: 8 }}
                    value={form.location}
                    onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    disabled={readOnly}
                    placeholder={tr('Préciser le lieu', 'Specify location')}
                  />
                )}
              </div>
              {canAssignStaff && (
                <div className="form-group">
                  <label className="form-label">{tr('Assigné à', 'Assigned to')}</label>
                  <select
                    className="form-select"
                    value={form.assignedTo}
                    onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}
                    disabled={readOnly}
                  >
                    <option value="">{tr('Non assigné', 'Unassigned')}</option>
                    {staff.map(s => (
                      <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{tr('Description', 'Description')} *</label>
              <textarea
                className="form-textarea"
                rows={3}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                required
                disabled={readOnly}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Actions immédiates', 'Immediate actions')}</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={form.immediateActions}
                  onChange={e => setForm(f => ({ ...f, immediateActions: e.target.value }))}
                  disabled={readOnly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Témoins', 'Witnesses')}</label>
                <input
                  className="form-input"
                  value={form.witnessNames}
                  onChange={e => setForm(f => ({ ...f, witnessNames: e.target.value }))}
                  disabled={readOnly}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 26 }}>
                  <input
                    type="checkbox"
                    checked={form.followupRequired}
                    onChange={e => setForm(f => ({ ...f, followupRequired: e.target.checked }))}
                    disabled={readOnly}
                  />
                  <span>{tr('Suivi requis', 'Follow-up required')}</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Date de suivi', 'Follow-up date')}</label>
                <input
                  type="date"
                  className="form-input"
                  value={form.followupDate}
                  onChange={e => setForm(f => ({ ...f, followupDate: e.target.value }))}
                  disabled={readOnly || !form.followupRequired}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Statut', 'Status')}</label>
                <select
                  className="form-select"
                  value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  disabled={readOnly}
                >
                  <option value="open">{tr('Ouvert', 'Open')}</option>
                  <option value="investigating">{tr('En investigation', 'Investigating')}</option>
                  <option value="resolved">{tr('Résolu', 'Resolved')}</option>
                  <option value="closed">{tr('Fermé', 'Closed')}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{tr('Notes de résolution', 'Resolution notes')}</label>
              <textarea
                className="form-textarea"
                rows={2}
                value={form.resolutionNotes}
                onChange={e => setForm(f => ({ ...f, resolutionNotes: e.target.value }))}
                disabled={readOnly}
              />
            </div>
        </form>
    </AccessibleModal>
  );
};

const IncidentsPage = () => {
  const { i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const tr = (fr, en) => (isFr ? fr : en);
  const { hasRole, user } = useAuth();
  const canCreate = hasRole('admin', 'manager', 'staff');
  const canEdit = hasRole('admin', 'manager');
  const canUpdateStatus = hasRole('admin', 'manager', 'staff');
  const canAssignStaff = hasRole('admin', 'manager');

  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    severity: '',
    incidentType: '',
    dateFrom: '',
    dateTo: '',
  });
  const [form, setForm] = useState({
    patientId: '',
    incidentType: 'fall',
    severity: 'medium',
    occurredAt: '',
    location: '',
    description: '',
    immediateActions: '',
    witnessNames: '',
    followupRequired: false,
    followupDate: '',
    assignedTo: '',
    locationPreset: '',
  });
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [modalIncident, setModalIncident] = useState(null);
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [openMenuIncidentId, setOpenMenuIncidentId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const patientsPromise = (() => {
        if (!(canCreate || canUpdateStatus)) return Promise.resolve({ data: [] });
        if (user?.role === 'staff' && user?.id) {
          return assignmentsAPI.getStaffPatients(user.id, { active: 'true' });
        }
        return patientsAPI.getAll({ limit: 500 });
      })();

      const [incidentsRes, patientsRes, staffRes] = await Promise.all([
        incidentsAPI.getAll({
          status: filters.status || undefined,
          severity: filters.severity || undefined,
          incidentType: filters.incidentType || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
        }),
        patientsPromise,
        canAssignStaff
          ? usersAPI.getAll({ role: 'staff', limit: 500 })
          : Promise.resolve({ data: { users: [] } }),
      ]);

      setIncidents(incidentsRes.data || []);
      if (user?.role === 'staff') {
        const rows = patientsRes.data || [];
        const dedup = [];
        const seen = new Set();
        rows.forEach((row) => {
          if (!row?.patient_id || seen.has(row.patient_id)) return;
          seen.add(row.patient_id);
          dedup.push({
            id: row.patient_id,
            first_name: row.patient_first_name || '',
            last_name: row.patient_last_name || '',
          });
        });
        setPatients(dedup);
      } else {
        setPatients(patientsRes.data || []);
      }
      setStaff(staffRes.data?.users || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    const closeMenu = () => setOpenMenuIncidentId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const handleSearch = () => {
    load();
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      severity: '',
      incidentType: '',
      dateFrom: '',
      dateTo: '',
    });
    setTimeout(() => load(), 0);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        patientId: form.patientId,
        incidentType: form.incidentType,
        severity: form.severity,
        occurredAt: form.occurredAt,
        location: form.location || null,
        description: form.description,
        immediateActions: form.immediateActions || null,
        witnessNames: form.witnessNames || null,
        followupRequired: form.followupRequired,
        followupDate: form.followupRequired && form.followupDate ? form.followupDate : null,
        assignedTo: form.assignedTo || null,
      };
      await incidentsAPI.create({
        ...payload,
      });

      setForm({
        patientId: '',
        incidentType: 'fall',
        severity: 'medium',
        occurredAt: '',
        location: '',
        description: '',
        immediateActions: '',
        witnessNames: '',
        followupRequired: false,
        followupDate: '',
        assignedTo: '',
        locationPreset: '',
      });
      await load();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || tr('Erreur lors de la création de l’incident', 'Error while creating incident'));
    }
  };

  const handleStatusUpdate = async (incident, status) => {
    const payload = { status };
    if (status === 'resolved' || status === 'closed') {
      const resolutionNotes = window.prompt(tr('Notes de résolution:', 'Resolution notes:'), '');
      if (resolutionNotes !== null) payload.resolutionNotes = resolutionNotes;
    }

    try {
      await incidentsAPI.updateStatus(incident.id, payload);
      await load();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || tr('Erreur lors de la mise à jour du statut', 'Error while updating status'));
    }
  };

  const handleCloseAsDelete = async (incident) => {
    if (!window.confirm(tr('Confirmer la suppression logique de cet incident ?', 'Confirm logical deletion of this incident?'))) return;
    try {
      await incidentsAPI.updateStatus(incident.id, {
        status: 'closed',
        resolutionNotes: incident.resolution_notes || tr('Clôturé manuellement', 'Closed manually'),
      });
      await load();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || tr('Erreur lors de la suppression', 'Error while deleting'));
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{tr('Incidents/Accidents', 'Incidents/Accidents')}</h2>
          <p className="text-muted">{incidents.length} {tr('incidents', 'incidents')}</p>
        </div>
      </div>

      <div className="card card-form" style={{ marginBottom: 16 }}>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <div className="form-row" style={{ width: '100%', margin: 0 }}>
            <div className="form-group">
              <label className="form-label">{tr('Statut', 'Status')}</label>
              <select className="form-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">{tr('Tous', 'All')}</option>
                <option value="open">{tr('Ouvert', 'Open')}</option>
                <option value="investigating">{tr('En investigation', 'Investigating')}</option>
                <option value="resolved">{tr('Résolu', 'Resolved')}</option>
                <option value="closed">{tr('Fermé', 'Closed')}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Sévérité', 'Severity')}</label>
              <select className="form-select" value={filters.severity} onChange={e => setFilters(f => ({ ...f, severity: e.target.value }))}>
                <option value="">{tr('Toutes', 'All')}</option>
                <option value="low">{tr('Faible', 'Low')}</option>
                <option value="medium">{tr('Moyenne', 'Medium')}</option>
                <option value="high">{tr('Élevée', 'High')}</option>
                <option value="critical">{tr('Critique', 'Critical')}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Type', 'Type')}</label>
              <select className="form-select" value={filters.incidentType} onChange={e => setFilters(f => ({ ...f, incidentType: e.target.value }))}>
                <option value="">{tr('Tous', 'All')}</option>
                <option value="fall">{tr('Chute', 'Fall')}</option>
                <option value="injury">{tr('Blessure', 'Injury')}</option>
                <option value="medical">{tr('Médical', 'Medical')}</option>
                <option value="behavioral">{tr('Comportement', 'Behavioral')}</option>
                <option value="medication">{tr('Médication', 'Medication')}</option>
                <option value="other">{tr('Autre', 'Other')}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Date et Heure (du)', 'Date and Time (from)')}</label>
              <input
                type="datetime-local"
                className="form-input"
                value={filters.dateFrom}
                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{tr('Date et Heure (au)', 'Date and Time (to)')}</label>
              <input
                type="datetime-local"
                className="form-input"
                value={filters.dateTo}
                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn btn-outline" onClick={handleResetFilters} disabled={loading}>
              {tr('Réinitialiser', 'Reset')}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSearch} disabled={loading}>
              {loading ? tr('Recherche...', 'Searching...') : tr('Rechercher', 'Search')}
            </button>
          </div>
        </div>
      </div>

      {canCreate && (
        <div className="card card-form" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>{tr('Déclarer un incident ou un accident', 'Report an incident or accident')}</h3>
          <form onSubmit={handleCreate}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Usager', 'Patient')} *</label>
                <select className="form-select" value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))} required>
                  <option value="">{tr('Sélectionner', 'Select')}</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Type', 'Type')} *</label>
                <select className="form-select" value={form.incidentType} onChange={e => setForm(f => ({ ...f, incidentType: e.target.value }))}>
                  <option value="fall">{tr('Chute', 'Fall')}</option>
                  <option value="injury">{tr('Blessure', 'Injury')}</option>
                  <option value="medical">{tr('Médical', 'Medical')}</option>
                  <option value="behavioral">{tr('Comportement', 'Behavioral')}</option>
                  <option value="medication">{tr('Médication', 'Medication')}</option>
                  <option value="other">{tr('Autre', 'Other')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Sévérité', 'Severity')} *</label>
                <select className="form-select" value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>
                  <option value="low">{tr('Faible', 'Low')}</option>
                  <option value="medium">{tr('Moyenne', 'Medium')}</option>
                  <option value="high">{tr('Élevée', 'High')}</option>
                  <option value="critical">{tr('Critique', 'Critical')}</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Date/heure', 'Date/time')} *</label>
                <input type="datetime-local" className="form-input" value={form.occurredAt} onChange={e => setForm(f => ({ ...f, occurredAt: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Lieu', 'Location')}</label>
                <select
                  className="form-select"
                  value={form.locationPreset}
                  onChange={e => {
                    const preset = e.target.value;
                    setForm(f => ({
                      ...f,
                      locationPreset: preset,
                      location: preset && preset !== MANUAL_LOCATION_VALUE ? preset : '',
                    }));
                  }}
                >
                  <option value="">{tr('Non précisé', 'Not specified')}</option>
                  {INCIDENT_LOCATION_OPTIONS.map(loc => (
                    <option key={loc} value={loc}>{locationLabel(loc, tr)}</option>
                  ))}
                </select>
                {form.locationPreset === MANUAL_LOCATION_VALUE && (
                  <input
                    className="form-input"
                    style={{ marginTop: 8 }}
                    value={form.location}
                    onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    placeholder={tr('Préciser le lieu', 'Specify location')}
                  />
                )}
              </div>
              {canAssignStaff && (
                <div className="form-group">
                  <label className="form-label">{tr('Assigné à', 'Assigned to')}</label>
                  <select className="form-select" value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}>
                    <option value="">{tr('Non assigné', 'Unassigned')}</option>
                    {staff.map(s => (
                      <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{tr('Description', 'Description')} *</label>
              <textarea className="form-textarea" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{tr('Actions immédiates', 'Immediate actions')}</label>
                <textarea className="form-textarea" rows={2} value={form.immediateActions} onChange={e => setForm(f => ({ ...f, immediateActions: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Témoins', 'Witnesses')}</label>
                <input className="form-input" value={form.witnessNames} onChange={e => setForm(f => ({ ...f, witnessNames: e.target.value }))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 26 }}>
                  <input type="checkbox" checked={form.followupRequired} onChange={e => setForm(f => ({ ...f, followupRequired: e.target.checked }))} />
                  <span>{tr('Suivi requis', 'Follow-up required')}</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">{tr('Date de suivi', 'Follow-up date')}</label>
                <input type="date" className="form-input" value={form.followupDate} onChange={e => setForm(f => ({ ...f, followupDate: e.target.value }))} disabled={!form.followupRequired} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" type="submit">+ {tr('Enregistrer incident', 'Save incident')}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{tr('Date et Heure', 'Date and Time')}</th>
                  <th>{tr('Usager', 'Patient')}</th>
                  <th>{tr('Type', 'Type')}</th>
                  <th>{tr('Sévérité', 'Severity')}</th>
                  <th>{tr('Statut', 'Status')}</th>
                  <th>{tr('Assigné', 'Assigned')}</th>
                  <th>{tr('Description', 'Description')}</th>
                  <th>{tr('Actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {incidents.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>{tr('Aucun incident', 'No incident')}</td></tr>
                )}
                {incidents.map(i => (
                  <tr key={i.id}>
                    <td>{i.occurred_at ? new Date(i.occurred_at).toLocaleString(isFr ? 'fr-CA' : 'en-CA') : '—'}</td>
                    <td>{i.patient_first_name} {i.patient_last_name}</td>
                    <td>{incidentTypeLabel(i.incident_type, tr)}</td>
                    <td><span className={badgeClassBySeverity(i.severity)}>{severityLabel(i.severity, tr)}</span></td>
                    <td><span className={badgeClassByStatus(i.status)}>{statusLabel(i.status, tr)}</span></td>
                    <td>{i.assigned_to_first_name ? `${i.assigned_to_first_name} ${i.assigned_to_last_name}` : '—'}</td>
                    <td style={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.description}</td>
                    <td>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuIncidentId(curr => (curr === i.id ? null : i.id));
                          }}
                          aria-label="Ouvrir le menu d'actions"
                          aria-haspopup="menu"
                          aria-expanded={openMenuIncidentId === i.id}
                        >
                          ⋮
                        </button>
                        {openMenuIncidentId === i.id && (
                          <div
                            role="menu"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: 'absolute',
                              top: '110%',
                              right: 0,
                              minWidth: 190,
                              background: 'white',
                              border: '1px solid var(--gray-200)',
                              borderRadius: 8,
                              boxShadow: 'var(--shadow-md)',
                              zIndex: 20,
                              padding: 6,
                            }}
                          >
                            <button
                              className="btn btn-sm btn-outline"
                              style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                              onClick={() => {
                                setModalIncident(i);
                                setModalReadOnly(true);
                                setShowIncidentModal(true);
                                setOpenMenuIncidentId(null);
                              }}
                            >
                              {tr('Consulter', 'View')}
                            </button>
                            {canEdit && (
                              <button
                                className="btn btn-sm btn-outline"
                                style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                onClick={() => {
                                  setModalIncident(i);
                                  setModalReadOnly(false);
                                  setShowIncidentModal(true);
                                  setOpenMenuIncidentId(null);
                                }}
                              >
                                {tr('Modifier', 'Edit')}
                              </button>
                            )}
                            {canUpdateStatus && (
                              <>
                                <button
                                  className="btn btn-sm btn-outline"
                                  style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                  onClick={() => {
                                    setOpenMenuIncidentId(null);
                                    handleStatusUpdate(i, 'investigating');
                                  }}
                                >
                                  {tr('Investiguer', 'Investigate')}
                                </button>
                                <button
                                  className="btn btn-sm btn-outline"
                                  style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                  onClick={() => {
                                    setOpenMenuIncidentId(null);
                                    handleStatusUpdate(i, 'resolved');
                                  }}
                                >
                                  {tr('Résoudre', 'Resolve')}
                                </button>
                              </>
                            )}
                            {canUpdateStatus && (
                              <button
                                className="btn btn-sm btn-danger"
                                style={{ width: '100%', justifyContent: 'flex-start' }}
                                onClick={() => {
                                  setOpenMenuIncidentId(null);
                                  handleCloseAsDelete(i);
                                }}
                              >
                                {tr('Supprimer', 'Delete')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showIncidentModal && modalIncident && (
        <IncidentModal
          incident={modalIncident}
          patients={patients}
          staff={staff}
          readOnly={modalReadOnly}
          canAssignStaff={canAssignStaff}
          onClose={() => setShowIncidentModal(false)}
          onSave={() => {
            setShowIncidentModal(false);
            load();
          }}
        />
      )}
    </div>
  );
};

export default IncidentsPage;
